import * as Phaser from 'phaser';
import { DESIGN } from '../config/design';
import { transitionManager } from '../systems/TransitionManager';

export class LoadingScene extends Phaser.Scene {
  private progressBar!: Phaser.GameObjects.Graphics;
  private progressText!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'LoadingScene' });
  }

  preload(): void {
    this.createBackground();
    this.createBrand();
    this.createProgressBar();
    this.createFloatingParticles();

    this.load.on('progress', (value: number) => {
      this.updateProgress(value);
    });

    this.load.on('complete', () => {
      this.time.delayedCall(600, () => this.enterGame());
    });
  }

  create(): void {
    // Loading is driven by load complete.
  }

  private enterGame(): void {
    const experimentVersion = this.registry.get('experimentVersion') ?? 'B';
    transitionManager.go(this, 'GameScene', { experimentVersion });
  }

  private createBackground(): void {
    const graphics = this.add.graphics();
    for (let y = 0; y < 720; y += 4) {
      const t = y / 720;
      const r = Math.floor(248 + (232 - 248) * t);
      const g = Math.floor(249 + (240 - 249) * t);
      const b = Math.floor(255 + (244 - 255) * t);
      graphics.fillStyle((r << 16) | (g << 8) | b, 1);
      graphics.fillRect(0, y, 1280, 4);
    }
  }

  private createBrand(): void {
    const title = this.add.text(640, 300, 'Toybox Arcade', {
      fontFamily: DESIGN.fonts.headline,
      fontSize: '64px',
      color: DESIGN.colors.primary,
      fontStyle: 'bold',
    }).setOrigin(0.5);

    title.setShadow(0, 4, 'rgba(132, 34, 40, 0.3)', 0, false, true);

    this.add.text(640, 380, 'Loading premium collectibles...', {
      fontFamily: DESIGN.fonts.body,
      fontSize: '18px',
      color: DESIGN.colors.onSurfaceVariant,
      letterSpacing: 2,
    }).setOrigin(0.5);
  }

  private createProgressBar(): void {
    const width = 360;
    const height = 20;
    const x = 640 - width / 2;
    const y = 440;

    const bgBar = this.add.graphics();
    bgBar.fillStyle(parseInt(DESIGN.colors.surfaceContainerHigh.replace('#', '0x'), 16), 1);
    bgBar.fillRoundedRect(x, y, width, height, 10);

    this.progressBar = this.add.graphics();

    this.progressText = this.add.text(640, 480, '0%', {
      fontFamily: DESIGN.fonts.body,
      fontSize: '16px',
      color: DESIGN.colors.onSurfaceVariant,
      fontStyle: 'bold',
    }).setOrigin(0.5);
  }

  private updateProgress(value: number): void {
    const width = 360;
    const height = 20;
    const x = 640 - width / 2;
    const y = 440;
    const pct = Math.round(value * 100);

    this.progressBar.clear();
    this.progressBar.fillStyle(parseInt(DESIGN.colors.primary.replace('#', '0x'), 16), 1);
    this.progressBar.fillRoundedRect(x, y, width * value, height, 10);
    this.progressBar.fillStyle(0xffffff, 0.3);
    this.progressBar.fillRoundedRect(x + 4, y + 2, width * value - 8, height / 2 - 2, 6);

    this.progressText.setText(`${pct}%`);
  }

  private createFloatingParticles(): void {
    const particleColors = [0xff7e7e, 0x88f2e0, 0xffe173, 0xa43a3d, 0x006b5f];

    for (let i = 0; i < 20; i++) {
      const color = particleColors[Math.floor(Math.random() * particleColors.length)];
      const x = Math.random() * 1280;
      const y = 720 + Math.random() * 200;
      const size = 4 + Math.random() * 8;
      const circle = this.add.circle(x, y, size, color, 0.6);

      this.tweens.add({
        targets: circle,
        y: -50,
        x: x + (Math.random() - 0.5) * 100,
        alpha: 0,
        duration: 3000 + Math.random() * 4000,
        delay: Math.random() * 3000,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }
}
